import { KeyboardDoubleArrowDown } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { Stack, styled, useMediaQuery, useTheme } from '@mui/system';
import { format, parseISO } from 'date-fns';
import { enUS, frCA } from 'date-fns/locale';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useEvent } from '@/contexts/EventsContext';

import PublicMilestoneTracker from './PublicMilestoneTracker';

const Overlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
}));

const Content = styled(Stack)(({ theme }) => ({
  position: 'relative',
  gap: theme.spacing(2),
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const AnimatedArrow = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  animation: 'bounce 2s infinite',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.common.white,
  fontSize: '2rem',

  '@keyframes bounce': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(10px)' },
  },
}));

const formatDateRange = (startAt, endAt, language) => {
  const locale = language === 'en' ? enUS : frCA;
  const startDate = parseISO(startAt);
  const endDate = parseISO(endAt);

  const formattedStartDate = format(startDate, 'MMMM do', { locale });
  const formattedEndDate = format(endDate, 'do yyyy', { locale });

  const formattedStartDateFR = format(startDate, 'd', { locale });
  const formattedEndDateFR = format(endDate, 'd MMMM yyyy', { locale });

  return language === 'en'
    ? `${formattedStartDate} to ${formattedEndDate}`
    : `${formattedStartDateFR} au ${formattedEndDateFR}`;
};

export default function CallToAction({ activeEvent = null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const videoRef = useRef(null);
  const { areDonationsOpen } = useEvent();
  const { t, i18n } = useTranslation();
  const hotelLink = t('hotel.link');
  const now = new Date();

  const volunteerFormUrl =
    !activeEvent
    || now < new Date(activeEvent.volunteerFormStartAt)
    || now > new Date(activeEvent.volunteerFormEndAt)
      ? ''
      : activeEvent.volunteerFormUrl;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: 'calc(100vh - 64px)',
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        preload="auto"
        width="1920"
        height="1080"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 0,
          filter: 'brightness(50%)',
        }}
      >
        <source src="/videos/nrb.webm" type="video/webm" />
      </video>
      <Overlay />
      <Content>
        <Typography variant="h3" component="div">
          <Image
            alt="Event Logo"
            width={isMobile ? 300 : 600}
            height={window.innerHeight}
            src={
              activeEvent?.logo
                ? `data:image/png;base64,${activeEvent?.logo}`
                : '/imgs/logo_main.png'
            }
          />
        </Typography>
        {activeEvent ? (
          <>
            <Typography gutterBottom variant="h5" className="font-oswald uppercase">
              {formatDateRange(activeEvent.startAt, activeEvent.endAt, i18n.language)}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              component="a"
              href="/events/guides/activities"
              className="font-oswald"
            >
              {t('cta.why')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="success"
              component="a"
              href="/#ticket-section"
              className="font-oswald"
            >
              {t('cta.ticket')}
            </Button>
            {hotelLink && (
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                component="a"
                href={hotelLink}
                className="font-oswald"
              >
                {t('cta.hotel')}
              </Button>
            )}

            {areDonationsOpen && (
              <Button
                fullWidth
                variant="contained"
                color="warning"
                component="a"
                href="/donations"
                className="font-oswald"
              >
                {t('cta.donate')}
              </Button>
            )}

            {areDonationsOpen && <PublicMilestoneTracker eventId={activeEvent?.id} />}

            {volunteerFormUrl && (
              <Button
                fullWidth
                variant="contained"
                color="warning"
                component="a"
                href={volunteerFormUrl}
                className="font-oswald"
              >
                {t('cta.volunteer')}
              </Button>
            )}
          </>
        ) : (
          <Button fullWidth variant="contained" color="primary" className="font-oswald">
            {t('cta.missed')}
          </Button>
        )}
        <AnimatedArrow>
          <Typography component="span">
            <KeyboardDoubleArrowDown />
          </Typography>
          <Typography component="span" className="font-oswald">
            {t('cta.scroll')}
          </Typography>
          <Typography component="span">
            <KeyboardDoubleArrowDown />
          </Typography>
        </AnimatedArrow>
      </Content>
    </Box>
  );
}

CallToAction.propTypes = {
  activeEvent: PropTypes.object,
};
